/**
 * RequireRetailerMember
 *
 * Gate for /retaileradmin: the user must be signed in (RequireAuth) AND hold an
 * approved retailer membership (the server decides — 403 not_member otherwise).
 * Renders the child UI once the retailer context is loaded.
 *
 * RequireManager — sub-gate for manager-only pages (Users).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, Store, ShieldAlert } from 'lucide-react';
import { RequireAuth } from '../RequireAuth';
import { RetailerAdminProvider, useRetailerAdmin, useRetailerContext } from './RetailerAdminContext';

const Gate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('retaileradmin');
  const { ctx, loading, error, reload } = useRetailerAdmin();

  if (loading && !ctx) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <Loader2 size={24} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} />
        <span className="visually-hidden">{t('gate.loading')}</span>
      </div>
    );
  }

  if (!ctx) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <div className="card shadow-sm" style={{ maxWidth: 440, width: '100%' }}>
          <div className="card-body p-4 text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-2"
              style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(13,110,253,0.08)' }}
            >
              <Store size={24} className="text-primary" />
            </div>
            <h4 className="fw-bold mb-1">{t('gate.notMemberTitle')}</h4>
            <p className="text-muted small mb-3">{error === 'failed' ? t('gate.loadFailed') : t('gate.notMemberBody')}</p>
            {error === 'failed' && (
              <button type="button" className="btn btn-outline-primary btn-sm mb-2" onClick={() => reload()}>{t('gate.retry')}</button>
            )}
            <div>
              <Link to="/account" className="btn btn-link btn-sm">{t('gate.backToAccount')}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export const RequireRetailerMember: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAuth>
    <RetailerAdminProvider>
      <Gate>{children}</Gate>
    </RetailerAdminProvider>
  </RequireAuth>
);

export const RequireManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  if (!ctx.canManage) {
    return (
      <div className="bg-white rounded-3 shadow-sm p-4">
        <div className="d-flex align-items-center gap-2 mb-2">
          <ShieldAlert size={20} className="text-warning" />
          <h4 className="fw-bold m-0">{t('gate.managerRequiredTitle')}</h4>
        </div>
        <p className="text-muted small mb-0">{t('gate.managerRequired')}</p>
      </div>
    );
  }
  return <>{children}</>;
};

export default RequireRetailerMember;
